/**
 * Payer Connections — the per-payer fold (AC-3, AC-4, AC-6, AC-8).
 *
 * Pure functions. No API calls, no side effects, no Date.now() — callers
 * that need "today" (e.g. the roster's days-blocked calculation) pass an
 * explicit `asOf`.
 */

import { resolveWhatsHappening } from './copy';
import { pickOpenTask, taskSummaryLine } from './tasks';
import {
  type EnrollmentRow,
  type EnrollmentStatus,
  type FoldedPayer,
  type FoldResult,
  type LiveEnrollmentStatusRow,
  type PayerState,
  type RailView,
  SEVERITY_ORDER,
  type StediStatusView,
} from './types';

/**
 * Severity rank — higher is more severe. Derived from SEVERITY_ORDER (not
 * hand-duplicated) so `mostSevere`/`pickWhatsHappeningRail` and
 * `sortPayers`/`summaryChipCounts` can never disagree about ordering.
 */
const SEVERITY_RANK = Object.fromEntries(
  SEVERITY_ORDER.map((state, index) => [state, SEVERITY_ORDER.length - 1 - index]),
) as Record<Exclude<PayerState, 'live'>, number>;

/**
 * Fold one rail's raw Stedi status to a state, per AC-3.
 * DRAFT -> needs_us. PROVIDER_ACTION_REQUIRED/REJECTED -> needs_customer.
 * STEDI_ACTION_REQUIRED/PROVISIONING/unknown -> waiting. LIVE -> ready.
 * CANCELED -> canceled (GDN-3684): terminal, nothing owed by anyone.
 */
export function foldStatus(status: EnrollmentStatus): Exclude<PayerState, 'live'> {
  switch (status) {
    case 'DRAFT':
      return 'needs_us';
    case 'CANCELED':
      return 'canceled';
    case 'PROVIDER_ACTION_REQUIRED':
    case 'REJECTED':
      return 'needs_customer';
    case 'LIVE':
      return 'ready';
    case 'STEDI_ACTION_REQUIRED':
    case 'PROVISIONING':
    default:
      return 'waiting';
  }
}

/** A payer's state is its most severe rail's state (AC-3). */
function mostSevere(states: Array<Exclude<PayerState, 'live'>>): Exclude<PayerState, 'live'> {
  return states.reduce(
    (worst, s) => (SEVERITY_RANK[s] > SEVERITY_RANK[worst] ? s : worst),
    SEVERITY_ORDER[SEVERITY_ORDER.length - 1] as Exclude<PayerState, 'live'>,
  );
}

/**
 * Group rows by payer. Grouping key is `stedi_payer_id`, falling back to
 * `payer_name` when missing/empty. Two distinct payers that both lack an id
 * and share a name would collide into one fold under this fallback — Stedi
 * normally populates the id, so this is a documented edge case, not a guard.
 *
 * The generated `LiveEnrollmentResponse` makes both fields nullable, so the
 * chain ends at `stedi_enrollment_id` (always present): a row with neither
 * id nor name still groups to itself rather than to an empty-string bucket
 * shared with every other nameless row. For every populated row the key is
 * unchanged from the hand-written contract.
 */
function groupKey(row: LiveEnrollmentStatusRow): string {
  return row.stedi_payer_id || row.payer_name || row.stedi_enrollment_id;
}

/** Display name for a payer group, with the same fallback chain as `groupKey`. */
function displayName(row: LiveEnrollmentStatusRow): string {
  return row.payer_name || row.stedi_payer_id || row.stedi_enrollment_id;
}

/** Project generated rows to the exact staff-facing diagnostic values. */
export function toStediStatusView(row: LiveEnrollmentStatusRow): StediStatusView {
  return {
    enrollmentId: row.stedi_enrollment_id,
    payerName: row.payer_name || row.stedi_payer_id || row.stedi_enrollment_id,
    transactionType: row.transaction_type,
    status: row.status,
    statusUpdatedAt: row.status_updated_at ?? null,
    stediProviderId: row.stedi_provider_id ?? null,
    providerNpi: row.provider_npi ?? null,
  };
}

/** Rail display order: 837P before 835 before anything else. */
const RAIL_TIEBREAK_ORDER = ['837P', '835'];

function railRank(transactionType: string): number {
  const index = RAIL_TIEBREAK_ORDER.indexOf(transactionType);
  return index === -1 ? RAIL_TIEBREAK_ORDER.length : index;
}

/**
 * Payer, then rail order, then provider, then enrollment id — a stable order
 * for display. The API returns one row per provider + payer + rail (the
 * newest attempt), but not in display order, so views and the fold sort here
 * rather than trusting response order.
 */
function displayRowOrder(a: LiveEnrollmentStatusRow, b: LiveEnrollmentStatusRow): number {
  return (
    groupKey(a).localeCompare(groupKey(b)) ||
    railRank(a.transaction_type) - railRank(b.transaction_type) ||
    (a.stedi_provider_id ?? '').localeCompare(b.stedi_provider_id ?? '') ||
    a.stedi_enrollment_id.localeCompare(b.stedi_enrollment_id)
  );
}

/** A sorted copy of the rows in display order; 835 and 837P, and distinct providers, stay separate. */
export function orderRowsForDisplay<Row extends LiveEnrollmentStatusRow>(rows: Row[]): Row[] {
  return [...rows].sort(displayRowOrder);
}

interface PayerGroup {
  payerId: string;
  payerName: string;
  rows: EnrollmentRow[];
}

function groupByPayer(rows: EnrollmentRow[]): PayerGroup[] {
  const groups = new Map<string, PayerGroup>();
  for (const row of rows) {
    const key = groupKey(row);
    const existing = groups.get(key);
    if (existing) {
      existing.rows.push(row);
    } else {
      groups.set(key, { payerId: key, payerName: displayName(row), rows: [row] });
    }
  }
  return [...groups.values()];
}

/**
 * "What's happening" for a payer is the most-severe rail's message (AC-6).
 * A tie in severity is broken by rail order: 837P before 835 before anything else.
 */
function pickWhatsHappeningRail(rails: RailView[]): RailView {
  return rails.slice().sort((a, b) => {
    const severityDiff = SEVERITY_RANK[b.state] - SEVERITY_RANK[a.state];
    if (severityDiff !== 0) return severityDiff;
    return railRank(a.transactionType) - railRank(b.transactionType);
  })[0];
}

/** Key rails by type; when several providers share a rail, by `<type>:<provider>` so none overwrites another. */
function keyRails(rails: RailView[]): Partial<Record<string, RailView>> {
  const byType = new Map<string, RailView[]>();
  for (const rail of rails) {
    byType.set(rail.transactionType, [...(byType.get(rail.transactionType) ?? []), rail]);
  }
  const keyed: Partial<Record<string, RailView>> = {};
  for (const [transactionType, group] of byType) {
    if (group.length === 1) {
      keyed[transactionType] = group[0];
      continue;
    }
    for (const rail of group) keyed[`${transactionType}:${rail.stediProviderId ?? ''}`] = rail;
  }
  return keyed;
}

/** A confirmed production 835 survives a partial lookup failure on another rail. */
function payerReceipt(rows: EnrollmentRow[]): EnrollmentRow['has_received_claim'] {
  if (rows.some(row => row.has_received_claim === true && row.evidence_source === 'received_835'))
    return true;
  if (
    rows.every(
      row =>
        row.has_received_claim === false && row.evidence_source === null && !row.evidence_error,
    )
  )
    return false;
  // Includes old deployed responses, inconsistent rails, and failed lookups.
  return null;
}

/** All rails must be LIVE and the same payer must have a verified production 835. */
export function foldPayers(rows: EnrollmentRow[]): FoldResult {
  const groups = groupByPayer(orderRowsForDisplay(rows));

  const counts: Record<PayerState, number> = {
    needs_us: 0,
    needs_customer: 0,
    waiting: 0,
    ready: 0,
    canceled: 0,
    live: 0,
  };

  const payers: FoldedPayer[] = [];
  let readyCount = 0;
  let unknownCount = 0;

  for (const group of groups) {
    const rails: RailView[] = group.rows.map(row => {
      const state = foldStatus(row.status);
      return {
        enrollmentId: row.stedi_enrollment_id,
        transactionType: row.transaction_type,
        status: row.status,
        state,
        statusMessage: row.status_message,
        rejectionReason: row.rejection_reason,
        submittedAt: row.submitted_at,
        createdAt: row.created_at,
        statusUpdatedAt: row.status_updated_at ?? null,
        stediProviderId: row.stedi_provider_id ?? null,
        providerNpi: row.provider_npi ?? null,
        portalUrl: row.portal_url,
        tasks: row.tasks ?? [],
      };
    });

    const foldedState = mostSevere(rails.map(r => r.state));

    const hasReceivedClaim = payerReceipt(group.rows);
    const isLive = foldedState === 'ready' && hasReceivedClaim === true;
    if (foldedState === 'ready' && hasReceivedClaim === false) readyCount += 1;
    // Receipt evidence is moot for a canceled payer; never ask anyone to re-check it.
    if (hasReceivedClaim === null && foldedState !== 'canceled') unknownCount += 1;
    const finalState: PayerState = isLive ? 'live' : foldedState;

    counts[finalState] += 1;

    if (finalState === 'live') continue;

    const whatsHappeningRail = pickWhatsHappeningRail(rails);
    // GDN-3992: an open task on the selected rail supplies the copy; it never
    // changes the state above. Only the selected rail's tasks are read; a
    // task on a less severe rail is not surfaced. `pickOpenTask` only returns
    // a task on PROVIDER_ACTION_REQUIRED (GDN-4026), so a waiting or ready
    // payer never gets one.
    const openTask = pickOpenTask(whatsHappeningRail.status, whatsHappeningRail.tasks);

    payers.push({
      payerId: group.payerId,
      payerName: group.payerName,
      state: finalState,
      hasReceivedClaim,
      rails: keyRails(rails),
      stediStatuses: group.rows.map(toStediStatusView),
      whatsHappening:
        foldedState === 'ready'
          ? hasReceivedClaim === null
            ? 'Connected.'
            : 'Connected. No claim confirmed as received by this payer yet.'
          : openTask
            ? taskSummaryLine(openTask.instructions)
            : resolveWhatsHappening(
                whatsHappeningRail.status,
                whatsHappeningRail.statusMessage,
                whatsHappeningRail.rejectionReason,
              ),
      ...(openTask && { openTask }),
      selectedRail: whatsHappeningRail,
      portalUrl: whatsHappeningRail.portalUrl,
    });
  }

  payers.sort(sortPayers);

  // A canceled payer is listed but is not enrolling.
  const enrollingCount = payers.length - counts.canceled;
  const liveCount = counts.live;

  return { payers, counts, enrollingCount, liveCount, readyCount, unknownCount };
}

/** AC-8: sort by state severity, then payer name. */
export function sortPayers(a: FoldedPayer, b: FoldedPayer): number {
  const severityDiff = SEVERITY_ORDER.indexOf(a.state) - SEVERITY_ORDER.indexOf(b.state);
  if (severityDiff !== 0) return severityDiff;
  return a.payerName.localeCompare(b.payerName);
}

/** AC-8: one summary chip per non-zero state, in severity order. */
export function summaryChipCounts(
  counts: Record<PayerState, number>,
): Array<{ state: (typeof SEVERITY_ORDER)[number]; count: number }> {
  return SEVERITY_ORDER.filter(state => counts[state] > 0).map(state => ({
    state,
    count: counts[state],
  }));
}
